// frontend\src\components\analytics\AnalyticsFilters.tsx
import React, { useRef, useState, useEffect, Dispatch, SetStateAction } from 'react';
import api from '../../utils/api';
import { SearchSuggestion, AnalyticsCompareMode } from '../../types';

const MAX_COMPARE_PERIODS = 5;
/** "Previous Month" lines the same days up in an earlier month, so it only makes sense for short ranges. */
const PREVIOUS_MONTH_MAX_DAYS = 30;

const inclusiveDayCount = (start: string, end: string): number | null => {
  if (!start || !end) return null;
  const startMs = new Date(`${start}T00:00:00`).getTime();
  const endMs = new Date(`${end}T00:00:00`).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return null;
  return Math.round((endMs - startMs) / (24 * 60 * 60 * 1000)) + 1;
};

const compareModeLabel = (mode: AnalyticsCompareMode): string =>
  mode === 'previousYear' ? 'Year' : mode === 'previousMonth' ? 'Month' : 'Period';

// Tags are held as a comma-separated list so more than one can be selected at a time. Before
// this, a second tag replaced the first, and the orphaned tag stayed in the search string —
// where the API then treated it as a product/customer name to match on.
const listValues = (list: string | null): string[] => (list ? list.split(',').filter(Boolean) : []);
const listIncludes = (list: string | null, value: string) => listValues(list).includes(value);
const addToList = (list: string | null, value: string): string =>
  listIncludes(list, value) ? (list as string) : [...listValues(list), value].join(',');
const removeFromList = (list: string | null, value: string): string | null => {
  const rest = listValues(list).filter((entry) => entry !== value);
  return rest.length > 0 ? rest.join(',') : null;
};

interface AnalyticsFiltersProps {
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  compareSearch: string;
  setCompareSearch: Dispatch<SetStateAction<string>>;
  timeBase: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  setTimeBase: Dispatch<SetStateAction<'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'>>;
  metric: 'sales' | 'orderCount' | 'itemCount';
  setMetric: Dispatch<SetStateAction<'sales' | 'orderCount' | 'itemCount'>>;
  startDate: string;
  setStartDate: Dispatch<SetStateAction<string>>;
  endDate: string;
  setEndDate: Dispatch<SetStateAction<string>>;
  compareMode: AnalyticsCompareMode;
  setCompareMode: Dispatch<SetStateAction<AnalyticsCompareMode>>;
  comparePeriods: number;
  setComparePeriods: Dispatch<SetStateAction<number>>;
  selectedTag: string | null;
  setSelectedTag: Dispatch<SetStateAction<string | null>>;
  selectedCompareTag: string | null;
  setSelectedCompareTag: Dispatch<SetStateAction<string | null>>;
  selectedCustomerType: string | null;
  setSelectedCustomerType: Dispatch<SetStateAction<string | null>>;
  selectedCompareCustomerType: string | null;
  setSelectedCompareCustomerType: Dispatch<SetStateAction<string | null>>;
  selectedSupplier: string | null;
  setSelectedSupplier: Dispatch<SetStateAction<string | null>>;
  selectedCompareSupplier: string | null;
  setSelectedCompareSupplier: Dispatch<SetStateAction<string | null>>;
}

const AnalyticsFilters: React.FC<AnalyticsFiltersProps> = ({
  search,
  setSearch,
  compareSearch,
  setCompareSearch,
  timeBase,
  setTimeBase,
  metric,
  setMetric,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  compareMode,
  setCompareMode,
  comparePeriods,
  setComparePeriods,
  selectedTag,
  setSelectedTag,
  selectedCompareTag,
  setSelectedCompareTag,
  selectedCustomerType,
  setSelectedCustomerType,
  selectedCompareCustomerType,
  setSelectedCompareCustomerType,
  selectedSupplier,
  setSelectedSupplier,
  selectedCompareSupplier,
  setSelectedCompareSupplier,
}) => {
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const compareSearchContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const compareSearchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [compareSearchQuery, setCompareSearchQuery] = useState<string>('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [compareSuggestions, setCompareSuggestions] = useState<SearchSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState<boolean>(false);
  const [compareSuggestionsLoading, setCompareSuggestionsLoading] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<SearchSuggestion[]>([]);
  const [compareSelectedItems, setCompareSelectedItems] = useState<SearchSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [showCompareSuggestions, setShowCompareSuggestions] = useState<boolean>(false);
  const [focusedSuggestionIndex, setFocusedSuggestionIndex] = useState<number>(-1);
  const [focusedCompareSuggestionIndex, setFocusedCompareSuggestionIndex] = useState<number>(-1);
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [tempStartDate, setTempStartDate] = useState<string>(startDate);
  const [tempEndDate, setTempEndDate] = useState<string>(endDate);
  const [tempCompareMode, setTempCompareMode] = useState<AnalyticsCompareMode>(compareMode);
  const [tempComparePeriods, setTempComparePeriods] = useState<number>(comparePeriods);

  const tempRangeDays = inclusiveDayCount(tempStartDate, tempEndDate);
  const previousMonthAllowed = tempRangeDays !== null && tempRangeDays < PREVIOUS_MONTH_MAX_DAYS;
  const compareDisabled = compareSelectedItems.length > 0;

  // Fall back off "Previous Month" as soon as the pending range grows past its limit.
  useEffect(() => {
    if (tempCompareMode === 'previousMonth' && !previousMonthAllowed) {
      setTempCompareMode('previousPeriod');
    }
  }, [tempCompareMode, previousMonthAllowed]);

  const openDatePicker = () => {
    if (!showDatePicker) {
      // Re-sync the pending values so the popover always opens on the applied selection.
      setTempStartDate(startDate);
      setTempEndDate(endDate);
      setTempCompareMode(compareMode);
      setTempComparePeriods(comparePeriods);
    }
    setShowDatePicker(!showDatePicker);
  };

  useEffect(() => {
    // Each box owns its own suggestion list, dropdown flag and loading flag. Sharing them
    // meant typing in the comparison box disabled the primary input and opened its dropdown.
    const fetchSuggestions = async (query: string, isCompare: boolean) => {
      const setTargetSuggestions = isCompare ? setCompareSuggestions : setSuggestions;
      const setTargetShow = isCompare ? setShowCompareSuggestions : setShowSuggestions;
      const setTargetLoading = isCompare ? setCompareSuggestionsLoading : setSuggestionsLoading;
      const setTargetFocused = isCompare ? setFocusedCompareSuggestionIndex : setFocusedSuggestionIndex;
      if (!query.trim()) {
        setTargetSuggestions([]);
        setTargetShow(false);
        setTargetFocused(-1);
        setTargetLoading(false);
        return;
      }
      setTargetLoading(true);
      try {
        const response = await api.get('/search', { params: { query } });
        setTargetSuggestions(response.data.suggestions || []);
        setTargetShow(true);
      } catch (error: any) {
        console.error('Search suggestions error:', error);
        setTargetSuggestions([]);
        setTargetShow(false);
      } finally {
        setTargetLoading(false);
      }
    };
    const debounce = setTimeout(() => {
      fetchSuggestions(searchQuery, false);
      fetchSuggestions(compareSearchQuery, true);
    }, 200);
    return () => clearTimeout(debounce);
  }, [searchQuery, compareSearchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node) &&
        compareSearchContainerRef.current &&
        !compareSearchContainerRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setShowCompareSuggestions(false);
        setFocusedSuggestionIndex(-1);
        setFocusedCompareSuggestionIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectedItems(
      search
        .split(',')
        .filter((v) => v)
        .map((value) => ({
          type:
            listIncludes(selectedTag, value) ? 'tag' :
            value === selectedSupplier ? 'supplier' :
            value === selectedCustomerType ? 'customer_type' :
            value.includes(' ') ? 'customer' : 'product',
          value,
          label: value,
        }))
    );
    setCompareSelectedItems(
      compareSearch
        .split(',')
        .filter((v) => v)
        .map((value) => ({
          type:
            listIncludes(selectedCompareTag, value) ? 'tag' :
            value === selectedCompareSupplier ? 'supplier' :
            value === selectedCompareCustomerType ? 'customer_type' :
            value.includes(' ') ? 'customer' : 'product',
          value,
          label: value,
        }))
    );
  }, [search, compareSearch, selectedTag, selectedCompareTag, selectedCustomerType, selectedCompareCustomerType]);

  const handleSelectItem = (item: SearchSuggestion, isCompare: boolean = false) => {
    const targetItems = isCompare ? compareSelectedItems : selectedItems;
    const setTargetItems = isCompare ? setCompareSelectedItems : setSelectedItems;
    const setTargetSearch = isCompare ? setCompareSearch : setSearch;
    const setTargetQuery = isCompare ? setCompareSearchQuery : setSearchQuery;
    const setTargetShowSuggestions = isCompare ? setShowCompareSuggestions : setShowSuggestions;
    const setTargetFocusedIndex = isCompare ? setFocusedCompareSuggestionIndex : setFocusedSuggestionIndex;
    const setTargetTag = isCompare ? setSelectedCompareTag : setSelectedTag;
    const setTargetSupplier = isCompare ? setSelectedCompareSupplier : setSelectedSupplier;
    const setTargetCustomerType = isCompare ? setSelectedCompareCustomerType : setSelectedCustomerType;
    const targetInputRef = isCompare ? compareSearchInputRef : searchInputRef;

    if (item.type === 'customer_type') {
      setTargetCustomerType(item.value);
      setTargetQuery('');
      setTargetShowSuggestions(false);
      setTargetFocusedIndex(-1);
      targetInputRef.current?.focus();
      return;
    }

    if (item.type === 'supplier') {
      setTargetSupplier(item.value);
      setTargetQuery('');
      setTargetShowSuggestions(false);
      setTargetFocusedIndex(-1);
      targetInputRef.current?.focus();
      return;
    }

    if (!targetItems.find((i) => i.value === item.value)) {
      const newSelectedItems = [...targetItems, item];
      setTargetItems(newSelectedItems);
      setTargetSearch(newSelectedItems.map((i) => i.value).join(','));
      if (item.type === 'tag') {
        setTargetTag((prev) => addToList(prev, item.value));
      }
      setTargetQuery('');
      setTargetShowSuggestions(false);
      setTargetFocusedIndex(-1);
      targetInputRef.current?.focus();
    }
  };

  const handleRemoveItem = (value: string, isCompare: boolean = false) => {
    const targetItems = isCompare ? compareSelectedItems : selectedItems;
    const setTargetItems = isCompare ? setCompareSelectedItems : setSelectedItems;
    const setTargetSearch = isCompare ? setCompareSearch : setSearch;
    const setTargetTag = isCompare ? setSelectedCompareTag : setSelectedTag;
    const setTargetSupplier = isCompare ? setSelectedCompareSupplier : setSelectedSupplier;
    const setTargetCustomerType = isCompare ? setSelectedCompareCustomerType : setSelectedCustomerType;
    const targetInputRef = isCompare ? compareSearchInputRef : searchInputRef;
    const newSelectedItems = targetItems.filter((i) => i.value !== value);
    setTargetItems(newSelectedItems);
    setTargetSearch(newSelectedItems.map((i) => i.value).join(','));
    // Drop the chip from the tag list too, so it never lingers as a plain search term.
    setTargetTag((prev) => removeFromList(prev, value));
    if ((isCompare ? selectedCompareSupplier : selectedSupplier) === value) {
      setTargetSupplier(null);
    }
    targetInputRef.current?.focus();
  };

  const handleRemoveCustomerType = (isCompare: boolean = false) => {
    const setTargetCustomerType = isCompare ? setSelectedCompareCustomerType : setSelectedCustomerType;
    setTargetCustomerType(null);
    const targetInputRef = isCompare ? compareSearchInputRef : searchInputRef;
    targetInputRef.current?.focus();
  };

  const handleRemoveSupplier = (isCompare: boolean = false) => {
    const setTargetSupplier = isCompare ? setSelectedCompareSupplier : setSelectedSupplier;
    setTargetSupplier(null);
    const targetInputRef = isCompare ? compareSearchInputRef : searchInputRef;
    targetInputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, isCompare: boolean = false) => {
    const targetSuggestions = isCompare ? compareSuggestions : suggestions;
    const targetShowSuggestions = isCompare ? showCompareSuggestions : showSuggestions;
    const setTargetFocusedIndex = isCompare ? setFocusedCompareSuggestionIndex : setFocusedSuggestionIndex;
    const focusedIndex = isCompare ? focusedCompareSuggestionIndex : focusedSuggestionIndex;
    const targetItems = isCompare ? compareSelectedItems : selectedItems;
    const targetQuery = isCompare ? compareSearchQuery : searchQuery;
    if (!targetShowSuggestions || targetSuggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setTargetFocusedIndex((prev) => Math.min(prev + 1, targetSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setTargetFocusedIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      e.preventDefault();
      handleSelectItem(targetSuggestions[focusedIndex], isCompare);
    } else if (e.key === 'Backspace' && !targetQuery && targetItems.length > 0) {
      e.preventDefault();
      handleRemoveItem(targetItems[targetItems.length - 1].value, isCompare);
    }
  };

  const handleMonthToDate = () => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    setTempStartDate(startOfMonth.toISOString().split('T')[0]);
    setTempEndDate(today.toISOString().split('T')[0]);
  };

  const handleYearToDate = () => {
    const today = new Date();
    const startOfYear = new Date(today.getFullYear(), 0, 1);
    setTempStartDate(startOfYear.toISOString().split('T')[0]);
    setTempEndDate(today.toISOString().split('T')[0]);
  };

  const handleConfirmDate = (skipAutoTimeBase = false) => {
    const start = new Date(tempStartDate);
    const end = new Date(tempEndDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (!skipAutoTimeBase && diffDays < 60) {
      setTimeBase('daily');
    }
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    if (!compareSelectedItems.length) {
      // A "Previous Month" comparison is meaningless once the range outgrows a month.
      const nextMode: AnalyticsCompareMode =
        tempCompareMode === 'previousMonth' && diffDays + 1 >= PREVIOUS_MONTH_MAX_DAYS
          ? 'previousPeriod'
          : tempCompareMode;
      setCompareMode(nextMode);
      setComparePeriods(tempComparePeriods);
    }
    setShowDatePicker(false);
  };

  return (
    <div className="filter-container" style={{ flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <button
            className="analytics-action-button"
            onClick={openDatePicker}
          >
            Select Dates
          </button>
          {showDatePicker && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '10px',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                minWidth: '230px',
                maxWidth: '260px',
              }}
            >
              <input
                type="date"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
                className="date-input"
              />
              <input
                type="date"
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
                className="date-input"
              />
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="analytics-action-button" onClick={handleMonthToDate}>Month To Date</button>
                <button className="analytics-action-button" onClick={handleYearToDate}>Year To Date</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>Compare To:</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <input
                      type="radio"
                      value="previousPeriod"
                      checked={tempCompareMode === 'previousPeriod'}
                      onChange={() => setTempCompareMode('previousPeriod')}
                      disabled={compareDisabled}
                    />
                    Previous Period
                  </label>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      opacity: previousMonthAllowed ? 1 : 0.5,
                    }}
                    title={
                      previousMonthAllowed
                        ? 'Matches the same days of the month in the earlier month'
                        : `Available for ranges under ${PREVIOUS_MONTH_MAX_DAYS} days`
                    }
                  >
                    <input
                      type="radio"
                      value="previousMonth"
                      checked={tempCompareMode === 'previousMonth'}
                      onChange={() => setTempCompareMode('previousMonth')}
                      disabled={compareDisabled || !previousMonthAllowed}
                    />
                    Previous Month
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <input
                      type="radio"
                      value="previousYear"
                      checked={tempCompareMode === 'previousYear'}
                      onChange={() => setTempCompareMode('previousYear')}
                      disabled={compareDisabled}
                    />
                    Previous Year
                  </label>
                </div>
                {!previousMonthAllowed && (
                  <span style={{ fontSize: '0.75rem', color: '#8F8F8A' }}>
                    Previous Month needs a range under {PREVIOUS_MONTH_MAX_DAYS} days.
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '0.875rem', fontWeight: 500 }} htmlFor="compare-periods">
                  Comparison Periods:
                </label>
                <select
                  id="compare-periods"
                  className="analytics-filter"
                  value={tempComparePeriods}
                  onChange={(e) => setTempComparePeriods(Number(e.target.value))}
                  disabled={compareDisabled}
                >
                  {Array.from({ length: MAX_COMPARE_PERIODS }, (_, i) => i + 1).map((count) => (
                    <option key={count} value={count}>
                      {count}
                    </option>
                  ))}
                </select>
                <span style={{ fontSize: '0.75rem', color: '#8F8F8A' }}>
                  Charts the {tempComparePeriods === 1 ? 'previous' : `${tempComparePeriods} previous`}{' '}
                  {compareModeLabel(tempCompareMode).toLowerCase()}
                  {tempComparePeriods === 1 ? '' : 's'} alongside the selection.
                </span>
              </div>
              <button
                className="analytics-action-button"
                onClick={() => handleConfirmDate()}
              >
                Update
              </button>
            </div>
          )}
        </div>
        <select
          value={metric}
          onChange={(e) => setMetric(e.target.value as 'sales' | 'orderCount' | 'itemCount')}
          className="analytics-filter"
        >
          <option value="sales">Sales ($)</option>
          <option value="orderCount">Orders</option>
          <option value="itemCount">Items Sold</option>
        </select>
        <select
          value={timeBase}
          onChange={(e) =>
            setTimeBase(e.target.value as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly')
          }
          className="analytics-filter"
        >
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
          <option value="yearly">Yearly</option>
        </select>
      </div>
      <div
        ref={searchContainerRef}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '400px',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '5px',
          minHeight: '38px',
          backgroundColor: '#fff',
        }}
      >
        {selectedItems.map((item) => (
          <span key={item.value} className={`analytics-tag ${item.type}`}>
            {item.label} ({item.type.replace('_', ' ')})
            <button className="analytics-tag-remove" onClick={() => handleRemoveItem(item.value)}>
              ×
            </button>
          </span>
        ))}
        {selectedCustomerType && (
          <span className="analytics-tag customer_type">
            {selectedCustomerType} (customer type)
            <button className="analytics-tag-remove" onClick={() => handleRemoveCustomerType()}>
              ×
            </button>
          </span>
        )}
        {selectedSupplier && (
          <span className="analytics-tag supplier">
            {selectedSupplier} (supplier)
            <button className="analytics-tag-remove" onClick={() => handleRemoveSupplier()}>
              ×
            </button>
          </span>
        )}
        <input
          ref={searchInputRef}
          type="text"
          placeholder={
            selectedItems.length === 0 ? 'Filter (e.g., John Doe, MPP-TM3-RUCA, Coilovers, Premium)' : ''
          }
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={(e) => handleKeyDown(e)}
          className="analytics-search-input"
        />
        {suggestionsLoading && searchQuery && (
          <span className="analytics-search-status">Searching…</span>
        )}
        {showSuggestions && suggestions.length > 0 && (
          <div className="analytics-dialog">
            <ul className="analytics-list">
              {suggestions.map((item, index) => (
                <li
                  key={`${item.type}-${item.value}`}
                  className={`analytics-item ${item.type} ${index === focusedSuggestionIndex ? 'focused' : ''}`}
                  onClick={() => handleSelectItem(item)}
                >
                  {item.label} ({item.type.replace('_', ' ')})
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div
        ref={compareSearchContainerRef}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '400px',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '5px',
          minHeight: '38px',
          backgroundColor: '#fff',
        }}
      >
        {compareSelectedItems.map((item) => (
          <span key={item.value} className={`analytics-tag ${item.type}`}>
            {item.label} ({item.type.replace('_', ' ')})
            <button className="analytics-tag-remove" onClick={() => handleRemoveItem(item.value, true)}>
              ×
            </button>
          </span>
        ))}
        {selectedCompareCustomerType && (
          <span className="analytics-tag customer_type">
            {selectedCompareCustomerType} (customer type)
            <button className="analytics-tag-remove" onClick={() => handleRemoveCustomerType(true)}>
              ×
            </button>
          </span>
        )}
        {selectedCompareSupplier && (
          <span className="analytics-tag supplier">
            {selectedCompareSupplier} (supplier)
            <button className="analytics-tag-remove" onClick={() => handleRemoveSupplier(true)}>
              ×
            </button>
          </span>
        )}
        <input
          ref={compareSearchInputRef}
          type="text"
          placeholder={
            compareSelectedItems.length === 0
              ? `Comparison Filter - Currently Comparing To Previous ${compareModeLabel(compareMode)}${
                  comparePeriods > 1 ? ` (x${comparePeriods})` : ''
                }`
              : ''
          }
          value={compareSearchQuery}
          onChange={(e) => setCompareSearchQuery(e.target.value)}
          onFocus={() => setShowCompareSuggestions(true)}
          onKeyDown={(e) => handleKeyDown(e, true)}
          className="analytics-search-input"
        />
        {compareSuggestionsLoading && compareSearchQuery && (
          <span className="analytics-search-status">Searching…</span>
        )}
        {showCompareSuggestions && compareSuggestions.length > 0 && (
          <div className="analytics-dialog">
            <ul className="analytics-list">
              {compareSuggestions.map((item, index) => (
                <li
                  key={`${item.type}-${item.value}`}
                  className={`analytics-item ${item.type} ${index === focusedCompareSuggestionIndex ? 'focused' : ''}`}
                  onClick={() => handleSelectItem(item, true)}
                >
                  {item.label} ({item.type.replace('_', ' ')})
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsFilters;